import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import EpisodeCard from "@/components/EpisodeCard";
import VideoModal from "@/components/VideoModal";
import {
  Calendar, Youtube, Music2, Facebook, Podcast, ChevronRight,
  Plane, Users, Heart, Cpu, TrendingUp, Sparkles, Building2, Cross,
} from "lucide-react";

// Official SVN Español hero artwork — complete composition: host + branding +
// DR flag + headline + tagline + Informados badge + category icons all baked in.
// Used everywhere the SVN Español show artwork is referenced (database
// artwork_url is also set to this URL so cards, episode thumbnails, search
// results, and admin all use the same image).
const HERO_ARTWORK =
  "https://customer-assets.emergentagent.com/job_svn-live/artifacts/44as86u2_espanol.png";

// Seven categories aligned to the artwork's icon row, in the same order.
const CATEGORIES = [
  { slug: "relaciones",     title: "Relaciones",     icon: Heart,      accent: "warm-gold",
    desc: ["Conversaciones reales", "sobre el amor,", "la familia y la vida."] },
  { slug: "cultura",        title: "Cultura",        icon: Building2,  accent: "ocean",
    desc: ["Nuestras raíces,", "nuestra gente,", "nuestra historia."] },
  { slug: "viajes",         title: "Viajes",         icon: Plane,      accent: "warm-gold",
    desc: ["Descubre el mundo", "con sabor", "dominicano."] },
  { slug: "negocios",       title: "Negocios",       icon: TrendingUp, accent: "warm-gold",
    desc: ["Ideas, emprendimiento", "y educación financiera", "para avanzar."] },
  { slug: "tecnologia",     title: "Tecnología",     icon: Cpu,        accent: "ocean",
    desc: ["Innovación, IA", "y herramientas para", "el futuro."] },
  { slug: "fe-y-proposito", title: "Fe y Propósito", icon: Cross,      accent: "purple",
    desc: ["Fe, crecimiento", "personal y propósito", "con dirección."] },
  { slug: "comunidad",      title: "Comunidad",      icon: Users,      accent: "red",
    desc: ["Unidos somos más.", "Juntos hacemos", "la diferencia."] },
];

const ACCENT_TEXT = {
  "warm-gold": "text-warm-gold",
  ocean: "text-ocean",
  purple: "text-[#a855f7]",
  red: "text-svn-red",
};

export default function Espanol() {
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/shows/svn-espanol").then((r) => setShow(r.data)).catch(() => setShow(false));
    api.get("/episodes", { params: { language: "es", limit: 50 } }).then((r) => setEpisodes(r.data));
  }, []);

  const artworkUrl = show?.artwork_url || HERO_ARTWORK;

  return (
    <div data-testid="espanol-page" className="bg-black">
      <VideoModal episode={playing} onClose={() => setPlaying(null)} />

      {/* ============================== HERO ==============================
          Full-bleed display of the new official artwork at its native framing.
          No text overlay — the artwork contains the headline, tagline,
          subheadline, "Informados/Inspirados/Conectados" badge, host, DR flag,
          and category iconography. */}
      <section className="relative bg-black" data-testid="espanol-hero">
        <img
          src={artworkUrl}
          alt="SVN Español — Diferentes Voces. Una Señal. Cultura. Conversación. Comunidad."
          className="w-full h-auto block"
          loading="eager"
          data-testid="espanol-hero-artwork"
        />
      </section>

      {/* ======================== CATEGORY ICON ROW ========================
          Seven categories in a tight row directly below the hero. Each shows
          the icon, the category name, and a 3-line description — matching the
          visual cadence baked into the artwork itself. */}
      <section className="border-t border-warm-gold/15 bg-black py-12 md:py-16" data-testid="espanol-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6 md:gap-4">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const textClass = ACCENT_TEXT[c.accent];
              return (
                <div key={c.slug} className="text-center md:text-left" data-testid={`espanol-cat-${c.slug}`}>
                  <Icon size={28} className={`${textClass} mx-auto md:mx-0`} strokeWidth={1.5} />
                  <p className={`mt-3 font-display font-black text-sm tracking-widest uppercase ${textClass}`}>
                    {c.title}
                  </p>
                  <div className="mt-2 space-y-0.5">
                    {c.desc.map((line) => (
                      <p key={line} className="text-xs text-neutral-300 font-body leading-snug">{line}</p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================= CADA SEMANA STRIP ========================= */}
      <section className="border-t border-warm-gold/15 bg-black py-10" data-testid="espanol-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-8 items-center">
          <div className="flex items-center gap-4">
            <Calendar size={36} className="text-svn-red shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-display font-black text-xs tracking-[0.25em] text-svn-red uppercase">
                Nuevos Episodios
              </p>
              <p className="font-display font-black text-2xl md:text-3xl tracking-tight text-white">
                Cada Semana
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400 mt-0.5">
                en <span className="text-warm-gold">SVN Español</span>
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="font-display text-3xl md:text-4xl font-black tracking-tight">
              <span className="text-gold">S</span><span className="text-svn-red">V</span><span className="text-white">N</span>
              <span className="ml-2 text-warm-gold font-display italic">Español</span>
            </p>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">
              Tu señal en español
            </p>
          </div>

          <div className="text-center md:text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-warm-gold mb-3">
              Escúchanos y míranos en
            </p>
            <div className="flex items-center justify-center md:justify-end gap-4">
              {show?.youtube_url ? (
                <a href={show.youtube_url} target="_blank" rel="noreferrer" aria-label="YouTube" className="text-svn-red hover:scale-110 transition" data-testid="espanol-link-youtube">
                  <Youtube size={28} />
                </a>
              ) : (
                <Youtube size={28} className="text-svn-red opacity-60" />
              )}
              <Podcast size={28} className="text-[#a855f7]" />
              {show?.spotify_url ? (
                <a href={show.spotify_url} target="_blank" rel="noreferrer" aria-label="Spotify" className="text-[#1db954] hover:scale-110 transition" data-testid="espanol-link-spotify">
                  <Music2 size={28} />
                </a>
              ) : (
                <Music2 size={28} className="text-[#1db954] opacity-60" />
              )}
              {show?.facebook_url ? (
                <a href={show.facebook_url} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-ocean hover:scale-110 transition" data-testid="espanol-link-facebook">
                  <Facebook size={28} />
                </a>
              ) : (
                <Facebook size={28} className="text-ocean opacity-60" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ======================= PROPÓSITO STRIP ======================= */}
      <section className="border-y border-warm-gold/15 bg-[#040608] py-6" data-testid="espanol-purpose">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs md:text-sm">
          <p className="font-display font-bold tracking-[0.3em] uppercase text-neutral-300">
            Una <span className="text-ocean">Señal.</span>{" "}
            Muchas <span className="text-white">Voces.</span>{" "}
            Un mismo <span className="text-svn-red">Propósito.</span>
          </p>
          <p className="font-display font-bold tracking-[0.3em] uppercase text-warm-gold">
            Orgullosamente Dominicanos 🇩🇴
          </p>
        </div>
      </section>

      {/* =========================== EPISODIOS =========================== */}
      <section id="episodios" className="py-20 md:py-24 bg-black" data-testid="espanol-episodes">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-white">
              Episodios
            </h2>
            <Link
              to="/episodes"
              className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white hover:text-warm-gold"
              data-testid="espanol-all-episodes"
            >
              Ver todo el catálogo <ChevronRight size={14} />
            </Link>
          </div>

          {episodes.length === 0 ? (
            <div className="border border-warm-gold/20 bg-[#0a0e12] p-12 text-center" data-testid="espanol-empty">
              <p className="text-warm-gold font-display text-2xl font-bold">Próximamente.</p>
              <p className="mt-3 text-neutral-400 font-body max-w-md mx-auto">
                Nuevos episodios en español están en camino. Suscríbete a la señal para enterarte primero.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((ep) => (
                <EpisodeCard key={ep.id} ep={ep} onPlay={setPlaying} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
